import { useEffect } from 'react';
import { getGuias, getGuiaById, getAgencias, saveGuia, vincularGuiaAgencia } from '../lib/guiasAgenciasStorage';
import {
  mergeCertificadosParaAdmin,
  type CertificadoParceiroRow,
  type PerfilSolicitacaoFornecedor,
} from '../../../shared/lib/solicitacaoFornecedorPerfil';
import type { TipoFornecedor } from '../lib/solicitacoesFornecedorStorage';
import { addSolicitacao } from '../lib/solicitacoesFornecedorStorage';
import { addCliente } from '../lib/clientesStorage';
import { syncClienteToApi } from '../lib/clientesApi';
import type { TipoDocumento } from '../../../shared/lib/clientePerfilTypes';
import { normalizeFotosTrabalhoFornecedor } from '../../../shared/lib/fotosTrabalhoFornecedor';

const MESSAGE_TYPE = 'naviex-guia-sync';
const MESSAGE_TYPE_SOLICITACAO = 'naviex-solicitacao-fornecedor';
const MESSAGE_TYPE_CLIENTE = 'naviex-cliente-registro';
const MESSAGE_PARCEIRO_CADASTRO_QUERY = 'naviex-parceiro-cadastro-query';
const MESSAGE_PARCEIRO_CADASTRO_REPLY = 'naviex-parceiro-cadastro-reply';

/**
 * Página que recebe atualizações do perfil do guia via postMessage.
 * O guia (em outra origem) abre esta página em iframe e envia os dados atualizados.
 * Atualiza o localStorage do admin para sincronização bidirecional.
 */
export default function GuiaSyncReceiverPage() {
  useEffect(() => {
    const postSolicitacaoReady = () => {
      try {
        if (window.parent !== window) {
          window.parent.postMessage({ type: MESSAGE_TYPE_SOLICITACAO + '-ready' }, '*');
        }
      } catch {
        /* ignore */
      }
    };
    const rafId = requestAnimationFrame(postSolicitacaoReady);
    const t1 = window.setTimeout(postSolicitacaoReady, 120);
    const t2 = window.setTimeout(postSolicitacaoReady, 400);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (msg?.type === MESSAGE_TYPE_SOLICITACAO && msg?.solicitacao) {
        try {
          const { nome, email, roles, provider, perfil } = msg.solicitacao as {
            nome?: string;
            email?: string;
            roles?: string[];
            provider?: string;
            perfil?: Record<string, unknown>;
          };
          if (nome && email && Array.isArray(roles) && roles.length > 0) {
            let perfilSolicitacao: PerfilSolicitacaoFornecedor | undefined;
            if (perfil && typeof perfil === 'object') {
              perfilSolicitacao = {
                cpf: String(perfil.cpf ?? '').trim(),
                telefone: String(perfil.telefone ?? '').trim(),
                chavePix: String(perfil.chavePix ?? '').trim(),
                baseCidadeId: String(perfil.baseCidadeId ?? '').trim(),
                imagemPerfil: perfil.imagemPerfil ? String(perfil.imagemPerfil) : undefined,
                fotosTrabalhoGuia: Array.isArray(perfil.fotosTrabalhoGuia)
                  ? (() => {
                      const x = normalizeFotosTrabalhoFornecedor(perfil.fotosTrabalhoGuia);
                      return x.length ? x : undefined;
                    })()
                  : undefined,
                fotosTrabalhoTerapeuta: Array.isArray(perfil.fotosTrabalhoTerapeuta)
                  ? (() => {
                      const x = normalizeFotosTrabalhoFornecedor(perfil.fotosTrabalhoTerapeuta);
                      return x.length ? x : undefined;
                    })()
                  : undefined,
                certificadoGuiaAnexo: perfil.certificadoGuiaAnexo ? String(perfil.certificadoGuiaAnexo) : undefined,
                certificadoGuiaDataConclusao: perfil.certificadoGuiaDataConclusao
                  ? String(perfil.certificadoGuiaDataConclusao)
                  : undefined,
                documentoIdentidadeFornecedor: perfil.documentoIdentidadeFornecedor
                  ? String(perfil.documentoIdentidadeFornecedor)
                  : undefined,
                documentoOpcionalAnexo: perfil.documentoOpcionalAnexo
                  ? String(perfil.documentoOpcionalAnexo)
                  : undefined,
                certificadoAnexo: perfil.certificadoAnexo ? String(perfil.certificadoAnexo) : undefined,
                certificadoDataConclusao: perfil.certificadoDataConclusao
                  ? String(perfil.certificadoDataConclusao)
                  : undefined,
                certificados: Array.isArray(perfil.certificados)
                  ? (perfil.certificados as { descricao?: string; anexo?: string; dataConclusao?: string; dataValidade?: string; nomeArquivo?: string; tipoDocumento?: string; outrosDetalhe?: string }[])
                      .filter((c) => String(c.anexo ?? '').trim() && String(c.descricao ?? '').trim())
                      .map((c): CertificadoParceiroRow => {
                        const dc = (c.dataConclusao || c.dataValidade || '').trim().slice(0, 10) || undefined;
                        const nomeArquivo = (c.nomeArquivo || '').trim() || undefined;
                        const td = c.tipoDocumento;
                        const tipoDocumento: CertificadoParceiroRow['tipoDocumento'] =
                          td === 'primeiros_socorros' || td === 'guia_condutor' || td === 'outros' ? td : undefined;
                        const outrosDetalhe = (c.outrosDetalhe || '').trim() || undefined;
                        return {
                          descricao: String(c.descricao ?? '').trim(),
                          anexo: String(c.anexo ?? '').trim(),
                          ...(nomeArquivo ? { nomeArquivo } : {}),
                          ...(dc ? { dataConclusao: dc } : {}),
                          ...(tipoDocumento ? { tipoDocumento } : {}),
                          ...(outrosDetalhe ? { outrosDetalhe } : {}),
                        };
                      })
                  : undefined,
                resumoPerfil: perfil.resumoPerfil ? String(perfil.resumoPerfil) : undefined,
                agenciaId: perfil.agenciaId != null ? String(perfil.agenciaId) : undefined,
              };
              if (perfilSolicitacao) {
                const certsNormalizados = mergeCertificadosParaAdmin(perfilSolicitacao);
                if (certsNormalizados.length > 0) perfilSolicitacao.certificados = certsNormalizados;
              }
            } else {
              perfilSolicitacao = undefined;
            }
            addSolicitacao({
              nome,
              email,
              roles: roles as TipoFornecedor[],
              provider: provider === 'google' ? 'google' : 'email',
              perfilSolicitacao,
            });
            if (event.source && typeof (event.source as MessageEvent['source'])?.postMessage === 'function') {
              (event.source as Window).postMessage({ type: MESSAGE_TYPE_SOLICITACAO + '-ack', ok: true }, '*');
            }
          }
        } catch (err) {
          console.error('[GuiaSyncReceiver] solicitacao', err);
        }
        return;
      }
      if (msg?.type === MESSAGE_PARCEIRO_CADASTRO_QUERY && typeof msg.email === 'string') {
        try {
          const email = msg.email.trim().toLowerCase();
          if (!email) return;
          const guia = getGuias().find((g) => (g.email || '').trim().toLowerCase() === email);
          const ag = getAgencias().find((a) => (a.email || '').trim().toLowerCase() === email);
          const cadastroStatus = guia?.status ?? ag?.status ?? null;
          const motivoInativo =
            guia?.motivoInatividade != null && String(guia.motivoInatividade).trim() !== ''
              ? String(guia.motivoInatividade).trim()
              : null;
          if (event.source && typeof (event.source as MessageEvent['source'])?.postMessage === 'function') {
            (event.source as Window).postMessage(
              {
                type: MESSAGE_PARCEIRO_CADASTRO_REPLY,
                email,
                cadastroStatus,
                motivoInativo,
                source: 'admin-local',
              },
              '*'
            );
          }
        } catch (err) {
          console.error('[GuiaSyncReceiver] parceiro-cadastro-query', err);
        }
        return;
      }
      if (msg?.type === MESSAGE_TYPE_CLIENTE && msg?.cliente) {
        try {
          const c = msg.cliente as Record<string, unknown>;
          const email = String(c.email ?? '').trim();
          if (!email) return;
          const tipoDoc = c.tipoDocumento ? String(c.tipoDocumento) : undefined;
          const data = {
            nome: String(c.nome ?? ''),
            sobrenome: String(c.sobrenome ?? ''),
            apelido: c.apelido ? String(c.apelido) : undefined,
            telefonePais: c.telefonePais ? String(c.telefonePais) : undefined,
            telefone: c.telefone ? String(c.telefone) : undefined,
            tipoDocumento: tipoDoc && ['CPF', 'CNH', 'RG', 'Passaporte'].includes(tipoDoc) ? (tipoDoc as TipoDocumento) : undefined,
            numeroDocumento: c.numeroDocumento ? String(c.numeroDocumento) : undefined,
            email,
            paisOrigem: c.paisOrigem ? String(c.paisOrigem) : undefined,
            estado: c.estado ? String(c.estado) : undefined,
            cidade: c.cidade ? String(c.cidade) : undefined,
            cep: c.cep ? String(c.cep) : undefined,
            endereco: c.endereco ? String(c.endereco) : undefined,
            sexo: c.sexo ? String(c.sexo) : undefined,
            dataNascimento: c.dataNascimento ? String(c.dataNascimento) : undefined,
            chavePix: c.chavePix ? String(c.chavePix) : undefined,
            provider: (c.provider === 'google' ? 'google' : 'email') as 'email' | 'google',
          };
          addCliente(data);
          void syncClienteToApi(data);
          if (event.source && typeof (event.source as MessageEvent['source'])?.postMessage === 'function') {
            (event.source as Window).postMessage({ type: MESSAGE_TYPE_CLIENTE + '-ack', ok: true }, '*');
          }
        } catch (err) {
          console.error('[GuiaSyncReceiver] cliente', err);
        }
        return;
      }
      if (msg?.type !== MESSAGE_TYPE || !msg.guia) return;
      try {
        const { guiaId, guia: updates } = msg;
        const guia = (guiaId && getGuiaById(guiaId)) || getGuias().find((g) => (g.email || '').toLowerCase() === (updates.email || '').toLowerCase());
        if (!guia) return;
        const novaBase = updates.baseCidadeId?.trim() || '';
        const pendenteEnviado = updates.baseCidadePendenteAprovacao?.trim() || '';
        const tinhaBase = !!(guia.baseCidadeId?.trim());
        const baseMudou = novaBase && novaBase !== guia.baseCidadeId;
        let baseCidadeId = guia.baseCidadeId;
        let baseCidadePendenteAprovacao = guia.baseCidadePendenteAprovacao;
        let nextStatus = guia.status;
        if (pendenteEnviado) {
          baseCidadePendenteAprovacao = pendenteEnviado;
          if (pendenteEnviado !== guia.baseCidadeId) nextStatus = 'inativo';
        }
        if (novaBase) {
          if (!tinhaBase) {
            baseCidadeId = novaBase;
            baseCidadePendenteAprovacao = undefined;
          } else if (baseMudou && !pendenteEnviado) {
            baseCidadePendenteAprovacao = novaBase;
            nextStatus = 'inativo';
          }
        }
        const u = updates as Record<string, unknown>;
        const rawCerts = u.certificados;
        const perfilLike: PerfilSolicitacaoFornecedor = {
          cpf: String(updates.cpf ?? guia.cpf ?? ''),
          telefone: String(updates.telefone ?? guia.telefone ?? ''),
          chavePix: String(updates.chavePix ?? guia.chavePix ?? ''),
          baseCidadeId: String(updates.baseCidadeId ?? guia.baseCidadeId ?? ''),
          certificados: Array.isArray(rawCerts)
            ? (rawCerts as { descricao?: string; anexo?: string; dataConclusao?: string; dataValidade?: string; nomeArquivo?: string; tipoDocumento?: string; outrosDetalhe?: string }[]).map((c): CertificadoParceiroRow => {
                const dc = (c.dataConclusao || c.dataValidade || '').trim().slice(0, 10) || undefined;
                const nomeArquivo = (c.nomeArquivo || '').trim() || undefined;
                const td = c.tipoDocumento;
                const tipoDocumento: CertificadoParceiroRow['tipoDocumento'] =
                  td === 'primeiros_socorros' || td === 'guia_condutor' || td === 'outros' ? td : undefined;
                const outrosDetalhe = (c.outrosDetalhe || '').trim() || undefined;
                return {
                  descricao: String(c.descricao ?? ''),
                  anexo: String(c.anexo ?? ''),
                  ...(nomeArquivo ? { nomeArquivo } : {}),
                  ...(dc ? { dataConclusao: dc } : {}),
                  ...(tipoDocumento ? { tipoDocumento } : {}),
                  ...(outrosDetalhe ? { outrosDetalhe } : {}),
                };
              })
            : undefined,
          certificadoGuiaAnexo: u.certificadoGuiaAnexo != null ? String(u.certificadoGuiaAnexo) : undefined,
          certificadoGuiaDataConclusao: u.certificadoGuiaDataConclusao != null ? String(u.certificadoGuiaDataConclusao) : undefined,
          certificadoAnexo: u.certificadoAnexo != null ? String(u.certificadoAnexo) : undefined,
          certificadoDataConclusao: u.certificadoDataConclusao != null ? String(u.certificadoDataConclusao) : undefined,
        };
        const certificadosMerged = mergeCertificadosParaAdmin(perfilLike);
        const certificadosFinal = certificadosMerged.length > 0 ? certificadosMerged : undefined;

        const docOp = u.documentoOpcionalAnexo as string | undefined;
        const docIdFornec = u.documentoIdentidadeFornecedor as string | undefined;
        const fotosGIn = u.fotosTrabalhoGuia;
        const fotosTIn = u.fotosTrabalhoTerapeuta;
        const resumoIn = u.resumoPerfil;
        const resumoPerfil =
          resumoIn !== undefined ? (String(resumoIn).trim() || undefined) : guia.resumoPerfil;
        const agenciaIn = u.agenciaId;
        const prevAg = guia.agenciaId ?? '';
        let nextAgenciaId = guia.agenciaId;
        if (agenciaIn !== undefined) {
          const t = String(agenciaIn).trim();
          nextAgenciaId = t || undefined;
        }

        const merged = {
          ...guia,
          nome: updates.nome ?? guia.nome,
          cpf: updates.cpf ?? guia.cpf,
          telefone: updates.telefone ?? guia.telefone,
          chavePix: updates.chavePix ?? guia.chavePix,
          baseCidadeId,
          baseCidadePendenteAprovacao,
          status: nextStatus,
          imagemPerfil: updates.imagemPerfil !== undefined ? updates.imagemPerfil : guia.imagemPerfil,
          fotosTrabalhoGuia:
            fotosGIn !== undefined
              ? (() => {
                  const x = normalizeFotosTrabalhoFornecedor(fotosGIn);
                  return x.length ? x : undefined;
                })()
              : guia.fotosTrabalhoGuia,
          fotosTrabalhoTerapeuta:
            fotosTIn !== undefined
              ? (() => {
                  const x = normalizeFotosTrabalhoFornecedor(fotosTIn);
                  return x.length ? x : undefined;
                })()
              : guia.fotosTrabalhoTerapeuta,
          documentoIdentificacao:
            docOp !== undefined ? (docOp || undefined) : guia.documentoIdentificacao,
          documentoIdentidadeFornecedor:
            docIdFornec !== undefined ? (docIdFornec || undefined) : guia.documentoIdentidadeFornecedor,
          certificadoAnexo: updates.certificadoAnexo !== undefined ? updates.certificadoAnexo : guia.certificadoAnexo,
          certificadoDataConclusao: updates.certificadoDataConclusao !== undefined ? updates.certificadoDataConclusao : guia.certificadoDataConclusao,
          certificados: certificadosFinal,
          resumoPerfil,
          agenciaId: nextAgenciaId,
        };
        saveGuia(merged);
        const nextAg = nextAgenciaId ?? '';
        if (prevAg !== nextAg) {
          vincularGuiaAgencia(guia.id, nextAgenciaId ?? null);
        }
        if (event.source && typeof (event.source as MessageEvent['source'])?.postMessage === 'function') {
          (event.source as Window).postMessage({ type: MESSAGE_TYPE + '-ack', ok: true }, '*');
        }
      } catch (err) {
        console.error('[GuiaSyncReceiver]', err);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div className="p-4 text-sm text-gray-500">
      Receptor de sincronização do guia.
    </div>
  );
}

export { MESSAGE_TYPE };
